import { useEffect, useState } from 'react';
import { Loader2, Sparkles, AlertTriangle } from 'lucide-react';
import { supabaseUrl, supabaseAnonKey } from '../services/supabaseClient';
import chinniRoboLogo from '../assets/chinni_robo.png';

export type ExamQuestionType =
  | 'mcq_single' | 'mcq_multi' | 'numerical' | 'assertion_reasoning'
  | 'short_answer' | 'long_answer' | 'case_based';

export interface ExplainResult {
  steps: string[];
  common_mistake?: string;
  ui_status: 'solid' | 'mostly_there' | 'needs_review';
  ui_message: string;
  detailed_model_answer?: string;
}

interface Props {
  question: string;
  // Optional + defaults to 'mcq_single' — pre-existing callers (DrillPage,
  // DrillArchive, MicroInteractionCard) predate the 7-question-type Test
  // Prep rewrite and never pass this; keeping it optional preserves them.
  questionType?: ExamQuestionType;
  optionA?: string; optionB?: string; optionC?: string; optionD?: string;
  correctIndex?: number;
  correctOptions?: number[];
  correctAnswerNumerical?: number;
  numericalTolerance?: number;
  assertionText?: string;
  reasonText?: string;
  modelAnswer?: string;
  caseStudyText?: string;
  studentSelected?: number;
  studentSelectedOptions?: number[];
  studentNumericalAnswer?: number;
  studentTextAnswer?: string;
  conceptName: string;
  gradeLevel: number;
  // Test Prep results (PracticeTestResults.tsx) want the explanation shown
  // immediately since the student is actively reviewing; every other
  // existing caller (Drill, MicroInteractionCard) keeps the original
  // click-to-reveal "Why this answer?" behavior unless it opts in.
  autoFetch?: boolean;
  // Bubbles the fetched result up so the results page can persist it (steps/
  // common_mistake/ui_status/ui_message/detailed_model_answer) via
  // record_practice_answer_insights once every question has resolved.
  onResult?: (result: ExplainResult) => void;
}

// Renders a step-by-step breakdown plus a "Common mistake" callout when
// relevant. Never renders any raw internal state label — only the
// already-translated ui_status/ui_message the edge function returns.
export default function ChinniExplainer({
  question, questionType = 'mcq_single',
  optionA, optionB, optionC, optionD,
  correctIndex, correctOptions, correctAnswerNumerical, numericalTolerance,
  assertionText, reasonText, modelAnswer, caseStudyText,
  studentSelected, studentSelectedOptions, studentNumericalAnswer, studentTextAnswer,
  conceptName, gradeLevel, autoFetch = false, onResult,
}: Props) {
  const [state, setState] = useState<'idle' | 'loading' | 'done' | 'error'>(autoFetch ? 'loading' : 'idle');
  const [result, setResult] = useState<ExplainResult | null>(null);

  async function ask() {
    setState('loading');
    try {
      const resp = await fetch(`${supabaseUrl}/functions/v1/explain-answer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify({
          question,
          question_type: questionType,
          option_a: optionA, option_b: optionB, option_c: optionC, option_d: optionD,
          correct_option_index: correctIndex,
          correct_options: correctOptions,
          correct_answer_numerical: correctAnswerNumerical,
          numerical_tolerance: numericalTolerance,
          assertion_text: assertionText,
          reason_text: reasonText,
          model_answer: modelAnswer,
          case_study_text: caseStudyText,
          student_selected: studentSelected,
          student_selected_options: studentSelectedOptions,
          student_numerical_answer: studentNumericalAnswer,
          student_text_answer: studentTextAnswer,
          concept_name: conceptName,
          grade_level: gradeLevel,
        }),
      });
      const data = await resp.json();
      if (!resp.ok || data.error) {
        setState('error');
        return;
      }
      const explainResult: ExplainResult = {
        steps: data.steps ?? [],
        common_mistake: data.common_mistake || undefined,
        ui_status: data.ui_status,
        ui_message: data.ui_message,
        detailed_model_answer: data.detailed_model_answer || undefined,
      };
      setResult(explainResult);
      setState('done');
      onResult?.(explainResult);
    } catch {
      setState('error');
    }
  }

  useEffect(() => {
    if (!autoFetch) return;
    let cancelled = false;
    ask().catch(() => { if (!cancelled) setState('error'); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question, questionType, autoFetch]);

  if (state === 'idle') {
    return (
      <button
        onClick={ask}
        className="flex items-center gap-1.5 text-xs text-coral-warm font-medium hover:underline mt-1"
      >
        <div className="w-4 h-4 rounded-full overflow-hidden flex-shrink-0">
          <img src={chinniRoboLogo} alt="" className="w-full h-full object-cover" />
        </div>
        Why this answer?
      </button>
    );
  }

  if (state === 'loading') {
    return (
      <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        Chinni is putting together the explanation…
      </div>
    );
  }

  if (state === 'error' || !result) {
    return (
      <p className="mt-2 text-xs text-red-500">
        Couldn't load the explanation. Please try again.
      </p>
    );
  }

  return (
    <div className="mt-3 rounded-lg border border-coral-warm/30 bg-orange-50 px-3 py-2.5">
      <div className="flex items-center gap-1.5 mb-2">
        <div className="w-4 h-4 rounded-full overflow-hidden flex-shrink-0">
          <img src={chinniRoboLogo} alt="Chinni" className="w-full h-full object-cover" />
        </div>
        <span className="text-xs font-semibold text-coral-warm flex items-center gap-1">
          Step-by-step explanation
          <Sparkles className="w-3 h-3" />
        </span>
      </div>

      {result.steps.length > 0 && (
        <ol className="list-decimal list-inside text-sm text-gray-800 space-y-1 mb-2">
          {result.steps.map((step, i) => (
            <li key={i}>{step}</li>
          ))}
        </ol>
      )}

      {result.detailed_model_answer && (
        <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap mb-2">{result.detailed_model_answer}</p>
      )}

      {result.common_mistake && (
        <div className="flex items-start gap-1.5 mt-2 rounded-md bg-amber-50 border border-amber-200 px-2.5 py-2">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800">
            <span className="font-semibold">Common mistake: </span>
            {result.common_mistake}
          </p>
        </div>
      )}

      <p className="text-xs text-gray-500 mt-2">{result.ui_message}</p>
    </div>
  );
}
